import { Platform } from "react-native";
import { uploadAsync, FileSystemUploadType } from "expo-file-system";
import { QuestInstances } from "@questmate/openapi-spec";
import {
  MediaFile,
  NativeFile,
  WebFile,
} from "@app/components/questkit/MediaSliderV2/MediaSliderTypes";

export type TransloadItAssemblyOptions =
  QuestInstances.GetItemInstanceFileUploadConfiguration.ResponseBody["assemblyOptions"];

const TL_REGION = "https://api2.transloadit.com";
const POLL_DELAY = 2_000; // 2 seconds

/**
 * Creates a new Transloadit assembly and uploads file contents to TransloadIt.
 * - Does not support resumable uploads.
 * - Resumable uploads send the file contents in a separate request and
 *   require supporting the tus protocol, which was nontrivial and didn't
 *   appear to have a good react-native library to perform the function for web and native.
 */
export async function createAssemblyAndUpload(
  options: TransloadItAssemblyOptions,
  _file: MediaFile
): Promise<TransloaditAssemblyStatusResponse> {
  if (Platform.OS === "web") {
    const file = _file as WebFile;
    const body = new FormData();
    body.append("params", options.params);
    body.append("signature", options.signature);
    body.append("file", file);

    const res = await fetch(`${TL_REGION}/assemblies`, {
      method: "POST",
      body,
    });

    if (!res.ok) {
      const errorText = await res.text().catch(() => "Unknown error");
      throw new Error(
        `Failed to create Transloadit assembly: ${res.status} ${res.statusText}. ${errorText}`
      );
    }

    return (await res.json()) as TransloaditAssemblyStatusResponse;
  } else {
    const file = _file as NativeFile;
    const result = await uploadAsync(
      "https://api2.transloadit.com/assemblies",
      file.uri,
      {
        // fieldName: "file",
        uploadType: FileSystemUploadType.MULTIPART,
        httpMethod: "POST",
        parameters: options,
      }
    );

    return JSON.parse(result.body) as TransloaditAssemblyStatusResponse;
  }
}

export type AssemblyStatus =
  /**
   * The Assembly was canceled.
   */
  | "ASSEMBLY_CANCELED"

  /**
   * The Assembly was completed successfully.
   */
  | "ASSEMBLY_COMPLETED"

  /**
   * The Assembly is currently being executed.
   */
  | "ASSEMBLY_EXECUTING"

  /**
   * The Assembly Notification was replayed.
   */
  | "ASSEMBLY_NOTIFICATION_REPLAYED"

  /**
   * The Assembly is being replayed.
   */
  | "ASSEMBLY_REPLAYING"

  /**
   * The Assembly is still in the process of being uploaded.
   */
  | "ASSEMBLY_UPLOADING"

  /**
   * The upload connection was closed or timed out before receiving all data.
   */
  | "REQUEST_ABORTED";

/**
 * Polls the Transloadit assembly until processing is complete
 */
export async function waitForAssembly(
  assemblyId: string
): Promise<
  | { ok: true; result: TransloaditAssemblyStatusResponse }
  | { ok: false; error: unknown }
> {
  try {
    if (!assemblyId) {
      throw new Error("Assembly ID is required");
    }

    let attempts = 0;
    const maxAttempts = 300; // 10 minutes with 2-second intervals

    while (attempts < maxAttempts) {
      attempts++;

      const res = await fetch(`${TL_REGION}/assemblies/${assemblyId}`);
      if (!res.ok) {
        throw new Error(
          `Failed to fetch assembly status: ${res.status} ${res.statusText}`
        );
      }

      const json = await res.json();
      const status: AssemblyStatus = json.ok;

      if (status === "ASSEMBLY_COMPLETED") {
        return { ok: true, result: json };
      }

      if (status === "ASSEMBLY_CANCELED" || status === "REQUEST_ABORTED") {
        return {
          ok: false,
          error: {
            ...json,
            message: `Assembly ${status.toLowerCase()}: ${
              json.message || "Unknown error"
            }`,
          },
        };
      }

      await new Promise((r) => setTimeout(r, POLL_DELAY));
    }

    throw new Error(`Assembly polling timeout after ${maxAttempts} attempts`);
  } catch (error) {
    if (error instanceof Error) {
      return {
        ok: false,
        error: { message: `Assembly polling failed: ${error.message}` },
      };
    }
    return {
      ok: false,
      error: { message: "Assembly polling failed: Unknown error occurred" },
    };
  }
}

export type AssemblyUpload = {
  as: null;
  basename: string;
  cost: number;
  exec_time: number;
  ext: string;
  field: string;
  from_batch_import: boolean;
  id: string;
  is_temp_url: boolean;
  is_tus_file: boolean;
  md5hash: string;
  meta: Record<string, unknown>;
  mime: string;
  name: string;
  original_basename: string;
  original_id: string;
  original_md5hash: string;
  original_name: string;
  original_path: string;
  queue: string;
  queue_time: number;
  size: number;
  ssl_url: string;
  tus_upload_url: null;
  type: string;
  url: string;
  user_meta: Record<string, unknown>;
};

type TransloaditAssemblyStatusResponse = {
  account_id: string;
  account_name: string;
  account_slug: string;
  api_auth_key_id: string;
  assembly_id: string;
  assembly_ssl_url: string;
  assembly_url: string;
  build_id: string;
  bytes_expected: number;
  bytes_received: number;
  bytes_usage: number;
  client_agent: null;
  client_ip: null;
  client_referer: null;
  companion_url: string;
  executing_jobs: unknown[];
  execution_duration: number;
  execution_start: string;
  fields: {
    itemId: string;
    questId: string;
    runId: string;
  };
  has_dupe_jobs: boolean;
  http_code: number;
  instance: string;
  is_infinite: boolean;
  jobs_queue_duration: number;
  last_job_completed: string;
  merged_params: string;
  message: string;
  notify_duration: null;
  notify_response_code: null;
  notify_response_data: null;
  notify_start: null;
  notify_url: null;
  num_input_files: number;
  ok: string;
  params: string;
  parent_assembly_status: null;
  parent_id: null;
  queue_duration: number;
  region: string;
  results: {
    [key: string]: Record<string, unknown>[][];
  };
  running_jobs: unknown[];
  start_date: string;
  started_jobs: string[];
  template: string;
  template_id: string;
  template_name: string;
  transloadit_client: null;
  tus_url: string;
  update_stream_url: string;
  upload_duration: number;
  upload_meta_data_extracted: boolean;
  uploads: AssemblyUpload[];
  uppyserver_url: string;
  usage_tags: string;
  warnings: unknown[];
  websocket_url: string;
};
